var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useParams } from "@madmedical/routing";
import { invariant, rangeToDate } from "@madmedical/utils";
import { skipToken, useDateRange } from "@madmedical/store";
import { useEffect, useState } from "react";
import { EvaluatingProcess, Spinner, useShowToast } from "@madmedical/ui";
import { usePatientId, useRouteNavigate } from "@madmedical/user";
import { DeleteDocumentDialog, useAttachDocumentMutation, useUploadDocumentMutation, } from "@madmedical/document";
import adaptOpinion from "../adaptOpinion";
import MeasurementProviders from "../../measurement/components/MeasurementProviders";
import { useCreateOpinionMutation, useGetOpinionQuery, useUpdateOpinionMutation, } from "../api";
var ModalMode;
(function (ModalMode) {
    ModalMode[ModalMode["Closed"] = 0] = "Closed";
    ModalMode[ModalMode["DeleteDocument"] = 1] = "DeleteDocument";
})(ModalMode || (ModalMode = {}));
const OpinionForm = () => {
    const { patientId } = usePatientId();
    const { opinionId } = useParams();
    const { metricType: metricTypeParam } = useParams();
    const { data: opinion, isLoading, refetch: refetchOpinion, } = useGetOpinionQuery(opinionId !== null && opinionId !== void 0 ? opinionId : skipToken);
    const [createOpinion, { isLoading: isCreateLoading }] = useCreateOpinionMutation();
    const [updateOpinion, { isLoading: isUpdateLoading }] = useUpdateOpinionMutation();
    const [upload, { isLoading: isUploadLoading }] = useUploadDocumentMutation();
    const [attach, { isLoading: isAttachLoading }] = useAttachDocumentMutation();
    const navigate = useRouteNavigate();
    const { setDateRange } = useDateRange();
    const [selectedRange, setSelectedRange] = useState(null);
    const [modalMode, setModalMode] = useState(ModalMode.Closed);
    const [idForModal, setIdForModal] = useState();
    const showToast = useShowToast();
    useEffect(() => {
        if (!opinion) {
            return;
        }
        setSelectedRange(rangeToDate(opinion.dateRange));
        setDateRange(opinion.extendedRange);
    }, [opinion, setDateRange]);
    const [isSaving, setIsSaving] = useState(false);
    useEffect(() => {
        // eslint-disable-next-line prefer-const
        let timeout;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            setIsSaving(isCreateLoading ||
                isUpdateLoading ||
                isUploadLoading ||
                isAttachLoading);
        }, 100);
        return () => clearTimeout(timeout);
    }, [isCreateLoading, isUpdateLoading, isUploadLoading, isAttachLoading]);
    const handleDeleteDocumentPress = (id) => {
        setIdForModal(id);
        setModalMode(ModalMode.DeleteDocument);
    };
    const handleModalClose = () => {
        void refetchOpinion()
            .unwrap()
            .then(() => {
            setModalMode(ModalMode.Closed);
        });
    };
    if (isLoading) {
        return _jsx(Spinner, {});
    }
    const resolvedMetricType = metricTypeParam !== null && metricTypeParam !== void 0 ? metricTypeParam : opinion === null || opinion === void 0 ? void 0 : opinion.metricType;
    invariant(resolvedMetricType);
    const handleSubmit = ({ text, alertLevel, newFiles }) => __awaiter(void 0, void 0, void 0, function* () {
        invariant(selectedRange);
        const commonPayload = {
            dateRange: selectedRange,
            text,
            alertLevel,
        };
        try {
            const opinionResult = yield (opinionId
                ? updateOpinion(Object.assign({ id: opinionId }, commonPayload))
                : createOpinion(Object.assign({ patientId, metricType: resolvedMetricType }, commonPayload))).unwrap();
            if (opinionResult) {
                if (newFiles.length) {
                    const formData = new FormData();
                    newFiles.forEach(({ file }) => formData.append("file[]", file));
                    const documents = yield upload(formData).unwrap();
                    const promises = documents.map((document, index) => attach({
                        id: document.id,
                        patientId,
                        opinionId: opinionResult.id,
                        note: newFiles[index].note,
                    }).unwrap());
                    yield Promise.all(promises);
                    // await refetchOpinion();
                }
                showToast("Sikeres mentés.", { variant: "success" });
                setTimeout(() => {
                    navigate("opinion", { opinionId: opinionResult.id }, { replace: false });
                }, 1000);
            }
        }
        catch (err) {
            showToast("Hiba történt.", { variant: "error" });
        }
    });
    return (_jsxs(MeasurementProviders, Object.assign({ metricType: resolvedMetricType, selectedRange: selectedRange, onSelectRange: setSelectedRange }, { children: [_jsx(EvaluatingProcess, { evaluation: opinion ? adaptOpinion(opinion) : undefined, metricType: resolvedMetricType, onSubmit: handleSubmit, onDeleteDocumentPress: handleDeleteDocumentPress, isSaving: isSaving, isUploading: isUploadLoading || isAttachLoading, isSubmitDisabled: !selectedRange }), _jsx(DeleteDocumentDialog, { id: idForModal, open: modalMode === ModalMode.DeleteDocument, onClose: handleModalClose })] })));
};
export default OpinionForm;
